import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Container, PageTitle, NbCard, NbButton, Chip } from "@/components/nb";
import { Stagger, Item } from "@/components/motion";
import StreakStrip from "@/components/StreakStrip";
import MilestoneWall from "@/components/MilestoneWall";
import { toast } from "sonner";
import { Save, Trophy, Sparkles } from "lucide-react";

const EMOJI_CHOICES = ["🚀", "🎯", "🧠", "⚡", "🔥", "🌟", "🦄", "🐼", "🦊", "🐙", "🌈", "🎨", "📚", "🧪", "🎧"];
const BG_CHOICES = [
  { id: "primary", cls: "bg-sl-primary" },
  { id: "secondary", cls: "bg-sl-secondary" },
  { id: "tertiary", cls: "bg-sl-tertiary" },
  { id: "accent", cls: "bg-sl-accent" },
  { id: "white", cls: "bg-white" },
];

export default function Profile() {
  const { user, setUserFromReset } = useAuth();
  const [form, setForm] = useState({ name: "", grade: "", avatar_emoji: "🚀", avatar_bg: "primary" });
  const [saving, setSaving] = useState(false);
  const [badges, setBadges] = useState([]);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      grade: user.grade || "",
      avatar_emoji: user.avatar_emoji || "🚀",
      avatar_bg: user.avatar_bg || "primary",
    });
  }, [user]);

  useEffect(() => {
    api.get("/users/badges").then((r) => { setBadges(r.data.badges || []); setAttempts(r.data.attempts || 0); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/users/me", form);
      if (setUserFromReset) setUserFromReset(r.data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const bgCls = BG_CHOICES.find((b) => b.id === form.avatar_bg)?.cls || "bg-sl-primary";

  return (
    <Container>
      <PageTitle eyebrow="PROFILE" title="This is you." sub="Tune your identity. Flex your badges." />
      <StreakStrip />
      <MilestoneWall streak={user?.streak} userName={user?.name || form.name} userId={user?.id} />

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Left: editable card */}
        <NbCard className="md:col-span-2 !p-6" data-testid="profile-form">
          <div className="flex items-center gap-4">
            <motion.div
              key={form.avatar_emoji + form.avatar_bg}
              initial={{ scale: 0.7, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 14 }}
              className={`w-24 h-24 rounded-2xl nb-border nb-shadow grid place-items-center text-5xl ${bgCls}`}
              data-testid="avatar-preview"
            >
              {form.avatar_emoji}
            </motion.div>
            <div>
              <div className="font-heading font-black text-2xl tracking-tight">{form.name || "Your name"}</div>
              <div className="font-mono-sl text-xs text-neutral-600">{user?.email}</div>
              {form.grade && <Chip color="tertiary">GRADE · {form.grade.toUpperCase()}</Chip>}
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="font-mono-sl text-xs uppercase">Name</label>
              <input data-testid="input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-4 py-3 nb-border rounded-lg bg-white" />
            </div>
            <div>
              <label className="font-mono-sl text-xs uppercase">Grade or year</label>
              <input data-testid="input-grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g. 10, College Y1" className="w-full mt-1 px-4 py-3 nb-border rounded-lg bg-white" />
            </div>

            <div>
              <label className="font-mono-sl text-xs uppercase">Pick an emoji</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="emoji-picker">
                {EMOJI_CHOICES.map((e) => (
                  <button key={e} onClick={() => setForm({ ...form, avatar_emoji: e })} data-testid={`emoji-${e}`}
                    className={`w-11 h-11 grid place-items-center text-2xl rounded-lg nb-border ${form.avatar_emoji === e ? "bg-sl-primary nb-shadow" : "bg-white"}`}>{e}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="font-mono-sl text-xs uppercase">Pick a background</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="bg-picker">
                {BG_CHOICES.map((b) => (
                  <button key={b.id} onClick={() => setForm({ ...form, avatar_bg: b.id })} data-testid={`bg-${b.id}`}
                    className={`w-11 h-11 rounded-lg nb-border ${b.cls} ${form.avatar_bg === b.id ? "nb-shadow ring-2 ring-black" : ""}`} aria-label={b.id} />
                ))}
              </div>
            </div>

            <NbButton onClick={save} color="black" disabled={saving} data-testid="save-profile" className="w-full">
              <Save className="w-4 h-4 inline-block mr-1" /> {saving ? "Saving..." : "Save profile"}
            </NbButton>
          </div>
        </NbCard>

        {/* Right: badges */}
        <div className="md:col-span-3">
          <NbCard className="!p-6 mb-6">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8" />
              <div>
                <div className="font-heading font-black text-2xl tracking-tight">Trophy wall</div>
                <div className="font-mono-sl text-xs text-neutral-600">{badges.length} unique · {attempts} attempts</div>
              </div>
            </div>
          </NbCard>

          {badges.length === 0 ? (
            <NbCard color="tertiary" className="!p-6" data-testid="empty-badges">
              <Sparkles className="w-6 h-6 mb-2" />
              <div className="font-heading font-black text-xl">No badges yet.</div>
              <p className="text-neutral-800 mt-1">Take a diagnostic and score fast to earn your first ⚡ Speed Master.</p>
            </NbCard>
          ) : (
            <Stagger className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="badges-grid">
              {badges.map((b) => (
                <Item key={b.id}>
                  <motion.div whileHover={{ y: -3 }} className="h-full">
                    <NbCard color={["primary", "secondary", "tertiary", "accent"][Math.abs(b.id.length) % 4]} className="!p-5 text-center h-full" data-testid={`badge-${b.id}`}>
                      <div className="text-5xl mb-1">{b.emoji}</div>
                      <div className="font-heading font-black tracking-tight">{b.title}</div>
                      <div className="font-mono-sl text-[10px] uppercase text-neutral-700 mt-1">EARNED {b.count}x</div>
                      {b.desc && <p className="text-xs text-neutral-800 mt-2 line-clamp-2">{b.desc}</p>}
                    </NbCard>
                  </motion.div>
                </Item>
              ))}
            </Stagger>
          )}
        </div>
      </div>
    </Container>
  );
}
